// NPM packages
import React, { useRef } from 'react';
import { Menu, useTheme } from '@mui/material';
import styled from '@emotion/styled';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCaretDown } from '@fortawesome/free-solid-svg-icons';

// All other imports

const StyledMenu = styled(Menu)(({ theme: { palette } }) => ({
  '&&&&&&&&& .Mui-focusVisible': {
    backgroundColor: palette.action.focus,
  },
  '&&&&&&&&& .MuiMenuItem-root:hover': {
    backgroundColor: palette.action.hover,
  },
  '&&&&&&&&& .MuiPaper-root': {
    borderRadius: '0px',
    border: '1px solid #dcdcdc',
    boxShadow: '1px 4px 5px #dcdcdc',
    marginTop: '4px',
  },
  '&&&&&&&&& .MuiMenuItem-root': {
    paddingLeft: '2.25rem',
    paddingRight: '2.25rem',
    display: 'flex',
    justifyContent: 'center',
  },
  '&&&&&&&&& .MuiList-padding': {
    paddingBottom: '2px',
  },
}));

const OpenIcon = styled(FontAwesomeIcon)(({ theme: { palette } }) => ({
  color: palette.common.white,
  paddingLeft: '0.5rem',
  paddingRight: '0.25rem',
  cursor: 'pointer',
  // Increase tap target size on touch screens
  boxSizing: 'content-box',
  padding: '0.4rem 0.4rem',
}));

export interface HoverMenuProps {
  id: string;
  renderAnchor: (anchorProps: object) => React.ReactNode;
  renderMenuItems: (args: { close: () => void }) => React.ReactNode;
}

/**
 * Dropdown menu that opens on hover (mouse) or keyboard (ArrowDown only).
 * Enter follows the anchor link. Accessible: focus returns to trigger on close.
 */
export default function HoverMenu(
  props: HoverMenuProps
): React.ReactElement | null {
  const { renderAnchor, renderMenuItems, id } = props;
  const menuRef = useRef<HTMLUListElement>(null);
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [keyboardTriggered, setKeyboardTriggered] = React.useState(false);
  const anchorElRef = useRef<HTMLElement | null>(null);
  const theme = useTheme();
  const open = Boolean(anchorEl);

  const caretRef = useRef<SVGSVGElement>(null);

  const handleCaretClick = (event: React.MouseEvent<SVGSVGElement>): void => {
    event.preventDefault();
    event.stopPropagation();
    if (open) {
      handleClose();
    } else {
      anchorElRef.current = caretRef.current as unknown as HTMLElement;
      setKeyboardTriggered(false);
      setAnchorEl(caretRef.current as unknown as HTMLElement);
    }
  };

  const handleOpenEvent = (event: React.MouseEvent<HTMLElement>): void => {
    anchorElRef.current = event.currentTarget;
    setKeyboardTriggered(false);
    setAnchorEl(event.currentTarget);
  };
  const handleKeyDown = (event: React.KeyboardEvent<HTMLElement>): void => {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      anchorElRef.current = event.currentTarget;
      setKeyboardTriggered(true);
      setAnchorEl(event.currentTarget);
    }
    // Enter and Space are left unhandled so the browser follows the anchor link naturally
  };
  const close = (): void => {
    setAnchorEl(null);
  };
  const handleClose = (): void => {
    const wasKeyboard = keyboardTriggered;
    setKeyboardTriggered(false);
    setAnchorEl(null);
    if (wasKeyboard && anchorElRef.current) {
      anchorElRef.current.focus();
    }
  };
  const handleMouseLeave = (event: React.MouseEvent<HTMLElement>): void => {
    const newTarget = event.relatedTarget;
    if (
      newTarget instanceof Node &&
      !(menuRef.current && menuRef.current.contains(newTarget))
    ) {
      close();
    }
  };
  const anchorId = `${id}-anchor`;
  const menuId = `${id}-menu`;
  return (
    <div style={{ display: 'flex', alignItems: 'center' }}>
      {renderAnchor({
        className: 'hover-menu-anchor',
        onMouseOver: handleOpenEvent,
        onKeyDown: handleKeyDown,
        onMouseLeave: handleMouseLeave,
        id: anchorId,
        'aria-controls': open ? menuId : undefined,
        'aria-haspopup': 'true',
        'aria-expanded': open ? 'true' : undefined,
        style: { zIndex: theme.zIndex.modal + 1, position: 'relative' },
      })}
      <OpenIcon
        icon={faCaretDown}
        size="sm"
        ref={caretRef}
        onClick={handleCaretClick}
        aria-label="Open menu"
        aria-controls={open ? menuId : undefined}
        aria-haspopup="true"
        aria-expanded={open ? 'true' : undefined}
      />
      <StyledMenu
        id={menuId}
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        disableScrollLock
        transitionDuration={0}
        MenuListProps={{
          'aria-labelledby': anchorId,
          onMouseLeave: handleMouseLeave,
          ref: menuRef,
          autoFocusItem: keyboardTriggered,
        }}
      >
        {renderMenuItems({
          close: handleClose,
        })}
      </StyledMenu>
    </div>
  );
}
